import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TitleWithHelp } from '../components/ui/help-tooltip';
import {
  ShieldCheck, Activity, Users, Lock, AlertTriangle, CheckCircle,
  XCircle, ChevronLeft, ChevronRight, RefreshCw, Filter, Search,
  Clock, Monitor, Globe
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const ACAO_LABELS = {
  login_success: 'Login OK',
  login_failed: 'Login Falhou',
  register: 'Registro',
  create: 'Criacao',
  update: 'Atualizacao',
  delete: 'Exclusao',
};

const ACAO_COLORS = {
  login_success: 'bg-emerald-100 text-emerald-700',
  login_failed: 'bg-red-100 text-red-700',
  register: 'bg-purple-100 text-purple-700',
  create: 'bg-blue-100 text-blue-700',
  update: 'bg-amber-100 text-amber-700',
  delete: 'bg-red-100 text-red-700',
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'];

const AuditDashboard = () => {
  const [resumo, setResumo] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtroAcao, setFiltroAcao] = useState('all');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroSucesso, setFiltroSucesso] = useState('all');

  const fetchResumo = useCallback(async () => {
    try {
      const { data } = await api.get('/auditoria/resumo');
      setResumo(data);
    } catch (err) {
      toast.error('Erro ao carregar resumo de auditoria');
    }
  }, []);

  const fetchEventos = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: 15 });
      if (filtroAcao && filtroAcao !== 'all') params.set('acao', filtroAcao);
      if (filtroUsuario) params.set('usuario', filtroUsuario);
      if (filtroSucesso !== 'all') params.set('sucesso', filtroSucesso === 'true');

      const { data } = await api.get(`/auditoria/eventos?${params}`);
      setEventos(data.eventos || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      toast.error('Erro ao carregar eventos');
    }
  }, [page, filtroAcao, filtroUsuario, filtroSucesso]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchResumo(), fetchEventos()]);
      setLoading(false);
    };
    load();
  }, [fetchResumo, fetchEventos]);

  const handleRefresh = () => {
    fetchResumo();
    fetchEventos();
    toast.success('Dados atualizados');
  };

  const kpis = resumo?.kpis || {};

  const activityData = (resumo?.atividade_diaria || []).map(d => ({
    data: d.data.slice(5),
    Sucesso: d.sucesso,
    Falha: d.falha,
  }));

  const resourceData = (resumo?.recursos_acessados || []).slice(0, 6).map(r => ({
    name: r.recurso || 'outros',
    value: r.acessos,
  }));

  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return ts; }
  };

  return (
    <div className="space-y-6" data-testid="audit-dashboard">
      <div className="flex items-center justify-between">
        <TitleWithHelp
          title="Auditoria de Seguranca"
          description="Painel de monitoramento de seguranca do sistema. Visualize logins, tentativas bloqueadas, acoes por usuario e eventos de auditoria em tempo real."
          connections={['Logs de Auditoria', 'Usuarios', 'Perfis de Acesso']}
        >
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="text-blue-600" size={28} />
            Auditoria de Seguranca
          </h1>
        </TitleWithHelp>
        <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="audit-refresh-btn">
          <RefreshCw size={16} className="mr-2" />
          Atualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="audit-kpis">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <Activity size={16} className="text-blue-500" />
              <span className="text-xs text-gray-500">Eventos 24h</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpis.total_eventos_24h ?? '-'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <Monitor size={16} className="text-indigo-500" />
              <span className="text-xs text-gray-500">Eventos 7d</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpis.total_eventos_7d ?? '-'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <Globe size={16} className="text-gray-500" />
              <span className="text-xs text-gray-500">Eventos 30d</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpis.total_eventos_30d ?? '-'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={16} className="text-emerald-500" />
              <span className="text-xs text-gray-500">Logins OK</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{kpis.logins_sucesso ?? '-'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <XCircle size={16} className="text-red-500" />
              <span className="text-xs text-gray-500">Logins Falha</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{kpis.logins_falha ?? '-'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <Lock size={16} className="text-orange-500" />
              <span className="text-xs text-gray-500">Bloqueadas</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{kpis.contas_bloqueadas_agora ?? 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-700">Atividade Diaria (7 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            {activityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="data" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Sucesso" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Falha" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
                Sem dados para exibir
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resources Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-700">Recursos Mais Acessados (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            {resourceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={resourceData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                    {resourceData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
                Sem dados para exibir
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Users */}
      {resumo?.usuarios_ativos?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Users size={16} />
              Usuarios Mais Ativos (30d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {resumo.usuarios_ativos.map((u, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-gray-800">{u.usuario}</span>
                  <Badge variant="secondary" className="text-xs">{u.acoes} acoes</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Events Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-sm font-semibold text-gray-700">
              Eventos de Auditoria ({total})
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap" data-testid="audit-filters">
              <Select value={filtroAcao} onValueChange={(v) => { setFiltroAcao(v); setPage(1); }}>
                <SelectTrigger className="w-[150px] h-8 text-xs">
                  <Filter size={12} className="mr-1" />
                  <SelectValue placeholder="Acao" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas acoes</SelectItem>
                  <SelectItem value="login_success">Login OK</SelectItem>
                  <SelectItem value="login_failed">Login Falhou</SelectItem>
                  <SelectItem value="create">Criacao</SelectItem>
                  <SelectItem value="update">Atualizacao</SelectItem>
                  <SelectItem value="delete">Exclusao</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filtroSucesso} onValueChange={(v) => { setFiltroSucesso(v); setPage(1); }}>
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="true">Sucesso</SelectItem>
                  <SelectItem value="false">Falha</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  className="h-8 text-xs pl-7 w-[160px]"
                  placeholder="Buscar usuario..."
                  value={filtroUsuario}
                  onChange={(e) => { setFiltroUsuario(e.target.value); setPage(1); }}
                  data-testid="audit-search-user"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="pb-2 pr-3">Data/Hora</th>
                  <th className="pb-2 pr-3">Usuario</th>
                  <th className="pb-2 pr-3">Acao</th>
                  <th className="pb-2 pr-3">Recurso</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {eventos.map((e, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 pr-3 text-xs text-gray-600 whitespace-nowrap">
                      <Clock size={12} className="inline mr-1 text-gray-400" />
                      {formatDate(e.timestamp)}
                    </td>
                    <td className="py-2 pr-3 text-xs font-medium text-gray-800 max-w-[180px] truncate">
                      {e.usuario_id || 'anonymous'}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge className={`text-xs ${ACAO_COLORS[e.acao] || 'bg-gray-100 text-gray-700'}`}>
                        {ACAO_LABELS[e.acao] || e.acao}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-600">
                      {e.recurso || '-'}
                      {e.recurso_id && <span className="text-gray-400 ml-1">({e.recurso_id.slice(0, 8)})</span>}
                    </td>
                    <td className="py-2 pr-3">
                      {e.sucesso ? (
                        <CheckCircle size={14} className="text-emerald-500" />
                      ) : (
                        <XCircle size={14} className="text-red-500" />
                      )}
                    </td>
                    <td className="py-2 text-xs text-gray-400">{e.ip_address || '-'}</td>
                  </tr>
                ))}
                {eventos.length === 0 && !loading && (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-400">Nenhum evento encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t" data-testid="audit-pagination">
              <span className="text-xs text-gray-500">
                Pagina {page} de {totalPages} ({total} registros)
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft size={14} />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditDashboard;
